/**
 * Document Types Admin Page
 * Sprint 2 / Sprint 11A: Updated with Layout component and Avida branding
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { documentTypesAPI } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  FileText,
  Plus,
  RefreshCw,
  AlertCircle,
  Edit,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Company", "Client", "Personnel"];
const TRIGGERS = [
  "None",
  "Admission",
  "Activation",
  "Assignment",
  "Ongoing",
  "Discharge",
];

function DocumentTypesPage() {
  const navigate = useNavigate();
  const {
    user,
    logout,
    canManageDocumentTypes,
    loading: authLoading,
  } = useAuth();
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    category: "Company",
    description: "",
    is_required: false,
    required_trigger: "None",
    expires: false,
    default_expiry_days: "",
  });

  const fetchDocTypes = async () => {
    try {
      const params = {};
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (!showInactive) params.is_active = true;

      const response = await documentTypesAPI.list(params);
      setDocTypes(response.data.document_types);
    } catch (err) {
      toast.error("Failed to load document types");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    fetchDocTypes();
  }, [authLoading, categoryFilter, showInactive]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDocTypes();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      category: "Company",
      description: "",
      is_required: false,
      required_trigger: "None",
      expires: false,
      default_expiry_days: "",
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = { ...formData };
      if (data.default_expiry_days) {
        data.default_expiry_days = parseInt(data.default_expiry_days);
      } else {
        delete data.default_expiry_days;
      }

      await documentTypesAPI.create(data);
      toast.success("Document type created");
      setCreateModalOpen(false);
      resetForm();
      fetchDocTypes();
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Failed to create document type";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    setSubmitting(true);

    try {
      const data = {};
      if (formData.name !== selectedType.name) data.name = formData.name;
      if (formData.description !== selectedType.description)
        data.description = formData.description;
      if (formData.is_required !== selectedType.is_required)
        data.is_required = formData.is_required;
      if (formData.required_trigger !== selectedType.required_trigger)
        data.required_trigger = formData.required_trigger;
      if (formData.expires !== selectedType.expires)
        data.expires = formData.expires;

      const expiryDays = formData.default_expiry_days
        ? parseInt(formData.default_expiry_days)
        : null;
      if (expiryDays !== selectedType.default_expiry_days)
        data.default_expiry_days = expiryDays;

      if (Object.keys(data).length === 0) {
        toast.info("No changes to save");
        setEditModalOpen(false);
        return;
      }

      await documentTypesAPI.update(selectedType.code, data);
      toast.success("Document type updated");
      setEditModalOpen(false);
      fetchDocTypes();
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Failed to update document type";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (code) => {
    try {
      await documentTypesAPI.update(code, { is_active: false });
      toast.success("Document type deactivated");
      fetchDocTypes();
    } catch (err) {
      toast.error("Failed to deactivate document type");
    }
  };

  const handleActivate = async (code) => {
    try {
      await documentTypesAPI.update(code, { is_active: true });
      toast.success("Document type activated");
      fetchDocTypes();
    } catch (err) {
      toast.error("Failed to activate document type");
    }
  };

  const openEditModal = (dt) => {
    setSelectedType(dt);
    setFormData({
      code: dt.code,
      name: dt.name,
      category: dt.category,
      description: dt.description || "",
      is_required: dt.is_required,
      required_trigger: dt.required_trigger,
      expires: dt.expires,
      default_expiry_days: dt.default_expiry_days
        ? String(dt.default_expiry_days)
        : "",
    });
    setEditModalOpen(true);
  };

  const getCategoryColor = (cat) => {
    const colors = {
      Company: "bg-blue-100 text-blue-700 border-blue-200",
      Client: "bg-green-100 text-green-700 border-green-200",
      Personnel: "bg-purple-100 text-purple-700 border-purple-200",
    };
    return colors[cat] || "bg-slate-100 text-slate-700";
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!canManageDocumentTypes) {
    return (
      <Layout title="Document Types">
        <div className="flex items-center justify-center py-12">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">
                Access Denied
              </h2>
              <p className="text-slate-600 mb-4">
                Admin or Compliance Manager access required.
              </p>
              <Button onClick={() => navigate("/")} variant="outline">
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Document Types">
      <div data-testid="document-types-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button
              className="bg-avida-blue hover:bg-avida-blue/90"
              onClick={() => {
                resetForm();
                setCreateModalOpen(true);
              }}
              data-testid="create-doctype-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Document Type
            </Button>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center space-x-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded"
              />
              <span>Show inactive</span>
            </label>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw
              className={
                refreshing ? "h-4 w-4 mr-1 animate-spin" : "h-4 w-4 mr-1"
              }
            />
            Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Document Types ({docTypes.length})</CardTitle>
            <CardDescription>
              Required document definitions for compliance
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                        Code
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                        Name
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                        Category
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                        Required
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                        Expires
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {docTypes.map((dt) => (
                      <tr
                        key={dt.code}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`doctype-row-${dt.code}`}
                      >
                        <td className="py-3 px-4 font-mono text-sm">
                          {dt.code}
                        </td>
                        <td className="py-3 px-4 text-slate-900">{dt.name}</td>
                        <td className="py-3 px-4">
                          <Badge
                            variant="outline"
                            className={getCategoryColor(dt.category)}
                          >
                            {dt.category}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {dt.is_required ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-slate-300" />
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {dt.expires ? (
                            <span className="text-sm text-amber-600">
                              {dt.default_expiry_days} days
                            </span>
                          ) : (
                            <span className="text-slate-400">No</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {dt.is_active ? (
                            <Badge
                              variant="outline"
                              className="bg-green-100 text-green-700"
                            >
                              Active
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-slate-100 text-slate-600"
                            >
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(dt)}
                              data-testid={`edit-${dt.code}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {dt.is_active ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeactivate(dt.code)}
                                className="text-red-600"
                                data-testid={`deactivate-${dt.code}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleActivate(dt.code)}
                                className="text-green-600"
                                data-testid={`activate-${dt.code}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {docTypes.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-8 text-center text-slate-500"
                        >
                          No document types found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Document Type</DialogTitle>
              <DialogDescription>
                Define a new document type for compliance tracking
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Code (uppercase, underscores)</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          code: e.target.value.toUpperCase(),
                        })
                      }
                      required
                      pattern="^[A-Z][A-Z0-9_]*$"
                      placeholder="CLIENT_RIGHTS_ACK"
                      data-testid="create-code"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) =>
                        setFormData({ ...formData, category: v })
                      }
                    >
                      <SelectTrigger data-testid="create-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    placeholder="Client Rights Acknowledgment"
                    data-testid="create-name"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Optional description..."
                    rows={2}
                    data-testid="create-description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.is_required}
                      onCheckedChange={(v) =>
                        setFormData({ ...formData, is_required: v })
                      }
                      data-testid="create-required"
                    />
                    <Label>Required</Label>
                  </div>
                  <div>
                    <Label>Required Trigger</Label>
                    <Select
                      value={formData.required_trigger}
                      onValueChange={(v) =>
                        setFormData({ ...formData, required_trigger: v })
                      }
                    >
                      <SelectTrigger data-testid="create-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRIGGERS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.expires}
                      onCheckedChange={(v) =>
                        setFormData({ ...formData, expires: v })
                      }
                      data-testid="create-expires"
                    />
                    <Label>Expires</Label>
                  </div>
                  {formData.expires && (
                    <div>
                      <Label>Default Expiry Days</Label>
                      <Input
                        type="number"
                        value={formData.default_expiry_days}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            default_expiry_days: e.target.value,
                          })
                        }
                        min="1"
                        placeholder="365"
                        data-testid="create-expiry-days"
                      />
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="create-submit"
                >
                  {submitting ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Document Type</DialogTitle>
              <DialogDescription>Code: {selectedType?.code}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  data-testid="edit-name"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={2}
                  data-testid="edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_required}
                    onCheckedChange={(v) =>
                      setFormData({ ...formData, is_required: v })
                    }
                    data-testid="edit-required"
                  />
                  <Label>Required</Label>
                </div>
                <div>
                  <Label>Required Trigger</Label>
                  <Select
                    value={formData.required_trigger}
                    onValueChange={(v) =>
                      setFormData({ ...formData, required_trigger: v })
                    }
                  >
                    <SelectTrigger data-testid="edit-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRIGGERS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.expires}
                    onCheckedChange={(v) =>
                      setFormData({ ...formData, expires: v })
                    }
                    data-testid="edit-expires"
                  />
                  <Label>Expires</Label>
                </div>
                {formData.expires && (
                  <div>
                    <Label>Default Expiry Days</Label>
                    <Input
                      type="number"
                      value={formData.default_expiry_days}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          default_expiry_days: e.target.value,
                        })
                      }
                      min="1"
                      data-testid="edit-expiry-days"
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={submitting}
                data-testid="edit-submit"
              >
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default DocumentTypesPage;
